import { ContentContainer } from 'components/city-blocks/content-container';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { FontSize } from 'web-common/src/shared/styles/font';

const CONTENT_BAY_AREA = (
  <ContentContainer fontSize={FontSize.M}>
    <span>
      The San Francisco Bay area is home to rich, fertile soil and abundant sea life. Those natural features have shaped
      local food culture and continue to remain at the center of California Cuisine. The word fusion is often used when
      referring to the food of San Francisco, and it's meant literally. The encounter between the native Ohlone Indians
      and the Spanish settlers coming up from Mexico led to a culinary melange that included colorful salsas, fideus
      with local shellfish, and carne asada.
    </span>
    <span>
      Yerba Buena became San Francisco in 1848 at the start of the Gold Rush. Jobs brought tradesmen from around the
      world. Europeans, Australians, and South Americans came in droves.
    </span>
    <span>
      Tea arrived from China, tobacco made its way up from Cuba, and the city constantly smelled of roasting coffee
      beans as New Yorkers came to caffeinate the growing workforce.
    </span>
    <span>
      This growing diversity brought us San Francisco favorites like Cioppino, Fortune Cookies, Sourdough Bread, and
      Hangtown Fry.
    </span>
    <span>
      Beyond the famed Michelin-starred eateries, the food culture of San Francisco centers around two ideas:
      sustainability and access. The 1969 Black Panthers Free School Lunch Program influenced an expansion of
      government-sponsored lunch programs for children across the country.
    </span>
    <span>
      In 1971, Alice Waters opened Chez Panisse, a restaurant that would come to define the farm-to-table movement in
      America. Her Edible Schoolyard Project has changed the face of school lunch, connecting children to local farmers.
    </span>
    <span>
      Today, San Francisco is home to the oldest Slow Food chapter in the United States. The Farmer's Market at the
      Ferry Building is where farmers who practice sustainable agriculture sell to a hungry, health-focused public. Run
      by the Center for Urban Education and Sustainable Agriculture, the goal isn't just profitability, but in equal
      measure, environmental health and economic equity.
    </span>
    <span>
      A city between mountains and surrounded by ocean, San Francisco represents a multi-cultural evolution of native
      ingredients and local flavors. From Shef Joon's Japchae noodles to Shef Joan's Goji Berry Sourdough Bread – this
      is the taste of San Francisco.
    </span>
  </ContentContainer>
);

const CONTENT_CHIGAGO = (
  <ContentContainer fontSize={FontSize.M}>
    <span>
      Serving iconic dishes such as deep-dish pizza and hotdogs, Chicago, Illinois, is a foodie's dream city. Dubbed the
      Best Restaurant City in 2017 by both Conde Nast Traveler and Bon Appetit, Chicago has a mix of different cuisines,
      all influenced by foreign immigrants and great migrations within the country.
    </span>
    <span>
      Back in 1943, during World War II, Pizzeria Uno opened and served the first iconic deep-dish pizza, changing how
      Chicagoans enjoyed a favorite comfort food. Just after the War, the Black Migration to Chicago brought many
      Southern dishes to the city and influenced the cuisines and restaurants. Jewish delicatessens started opening
      around the city as well, serving sandwiches, brisket, and other deli dishes. Fast forward to the 1960s, and we see
      more international communities in Chicago, such as Mexican, Greek, and Chinese, opening restaurants and serving
      traditional dishes to residents.
    </span>
    <span>
      Today, Chicago is thriving with international cuisine restaurants as well as local food such as hotdogs, deep-dish
      pizza, Garrett popcorn, and Italian beef. You can find a delightful mix of neighborhood corner stores and
      Michelin-star restaurants on the streets of Chicago to satisfy any food cravings you may encounter.
    </span>
    <span>
      The most authentic way to experience the culinary history of Chicago is to be invited to the kitchen of a local.
      There, you can taste generations-old recipes that have evolved to adapt to the ingredients of the Pacific Midwest.
      From Shef Rohini's Kashmiri Rajmah to Shef Hyunjung's Korean Bulgogi – this is the taste of Chicago.
    </span>
  </ContentContainer>
);

const CONTENT_LOS_ANGELES = (
  <ContentContainer fontSize={FontSize.M}>
    <span>
      One thing hasn't changed about LA since its founding in 1781. The countless taco trucks and burrito bars are your
      first hint, and the scent of chilies roasting in the warm breeze gives it away. When the Spanish rode up from
      Mexico to lay claim to Los Angeles, the food culture of Latin America was coming with them.
    </span>
    <span>
      The Second Great Migration in the 1940s and 50s brought soul food to southern California from Texas, Georgia, and
      Louisiana. They introduced beautifully complex spice blends, BBQ sauces, Creole dishes, and Caribbean flavors, and
      helped birth LA's obsession with hot sauce.
    </span>
    <span>
      Food culture continued to transform in the 1960s and 1970s as a mass influx of American war refugees from Korea
      and Vietnam arrived. Political refugees from Southeast Asia followed, and civil war refugees from Latin America
      after that. Because these immigrant neighborhoods were packed so tightly together, creative flavors began to pop
      up everywhere. Hybrid-style tacos, Korean BBQ, and dumplings of all shapes and flavors.
    </span>
    <span>
      Today, the Alta-California cuisine of Los Angeles merges native culinary history with the early days of San
      Francisco. It's about utilizing ingredients like sweet potatoes and sea urchins and putting them on a tostada. It
      also pays homage to the long and hot history of spice purveyors that has helped shape the unique and spicy flavors
      of Los Angeles.
    </span>
    <span>
      The only place to experience the many cultures, histories, and foods of Los Angeles is at the dinner table of
      locals. At Shef, you are invited to those tables where you can taste generations-old recipes that have evolved to
      incorporate the native ingredients of Southern California. From Shef Suz's Nepalese Momo's to Shef Adriana's
      Nicaraguan Beef Empanadas – this is the taste of Los Angeles.
    </span>
  </ContentContainer>
);

const CONTENT_NEW_YORK = (
  <ContentContainer fontSize={FontSize.M}>
    <span>
      To understand the important role that food plays in the history of New York, you need to know the name Amelia
      Simmons. What's known about her can be summed up on the cover page of her 1796 cookbook American Cookery. She
      calls herself an “American orphan”. Lack of education and opportunity likely meant that she was a domestic worker
      somewhere near the Hudson Valley. But in that work, she learned a lot. Enough to write and publish this country's
      very first cookbook, a book that The Library of Congress has designated one of the 88 “Books That Shaped America.”
    </span>
    <span>
      Amelia's is just one story woven into a colorful fabric of flavors. She was reimagining British foods to create
      uniquely New England flavors. Much the way new arrivals from Southern Italy were adapting the recipes of their
      homeland to the ingredients that were available in their New York neighborhoods (spaghetti with meatballs, chicken
      parmesan, fettucini alfredo). And the way Irish arrivals were transforming dishes like traditional bacon and
      cabbage into corned beef and cabbage and soda bread with “emigrants' bread”. In modern times, the same process is
      taking place with the arrival of immigrants from the Dominican Republic, China, Mexico, Jamaica, and India, among
      others.
    </span>
    <span>
      Like everywhere else in America, this food comes from somewhere else. It's inspired by nostalgia for places and
      cultures from afar. New Yorkers turn something traditional into something original in their attempts to recreate
      the flavors of where they came from. This vibrant, multicultural city is the great buffet of the world, offering
      up a taste of culinary heritage from every nook and cranny of the globe. From Shef Latoya's Jamaican Jerk Chicken
      to Shef Becky's Truffle Edamame dumplings – this is the taste of New York.
    </span>
  </ContentContainer>
);

const CONTENT_SEATTLE = (
  <ContentContainer fontSize={FontSize.M}>
    <span>
      The food and restaurant scene in Seattle, Washington, is thriving and satisfying the cravings of both residents
      and visitors alike. With diverse restaurant options, large food markets such as the famous Pike Place Market, and
      of course, the birthplace of Starbucks, food lovers can eat their way through Seattle and try both local and
      international cuisines.
    </span>
    <span>
      Seattle's notorious food scene started with the World's Fair in 1962. It introduced delicious international
      cuisine, with Belgian waffles being the most popular. Residents fell in love with German, Mexican, Thai, French,
      Creole, Korean, Japanese, and more cuisines. The iconic Space Needle restaurant resulted from the World's Fair.
    </span>
    <span>
      Seattle locals became more excited and adventurous with their diets and food that international restaurants began
      popping up around the city and even resulting in the building of Piers 50 and 51 to be lined with exotic places to
      eat.
    </span>
    <span>
      The most authentic way to experience the culinary history of Seattle is to be invited to the kitchen of a local.
      There, you can taste generations-old recipes that have evolved to adapt to the ingredients of the Pacific
      Northwest. From Shef Tirhas's Ethiopian Chicken Stew to Shef Samuel's Salvadorian Papusas – this is the taste of
      Seattle.
    </span>
  </ContentContainer>
);

export const generateRegionContentCopy = (region: Pick<RegionInfoFragment, 'name'>) => {
  switch (region.name) {
    case 'BAY_AREA':
      return CONTENT_BAY_AREA;
    case 'CHICAGO':
      return CONTENT_CHIGAGO;
    case 'LOS_ANGELES':
      return CONTENT_LOS_ANGELES;
    case 'NEW_YORK':
      return CONTENT_NEW_YORK;
    case 'SEATTLE':
      return CONTENT_SEATTLE;
    default:
      return null; // No fallback copy - just omit
  }
};
